import React from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import { Build, Refresh } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const MaintenancePage: React.FC = () => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      dir={isArabic ? 'rtl' : 'ltr'}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background circles */}
      <Box sx={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        background: 'rgba(255, 152, 0, 0.05)', top: -50, left: -50,
        animation: 'pulse 4s ease-in-out infinite',
        '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.2)' } }
      }} />
      <Box sx={{
        position: 'absolute', width: 200, height: 200, borderRadius: '50%',
        background: 'rgba(255, 152, 0, 0.03)', bottom: -30, right: -30,
        animation: 'pulse 5s ease-in-out infinite',
      }} />

      <Stack
        spacing={4}
        alignItems="center"
        sx={{
          textAlign: 'center',
          p: 6,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 152, 0, 0.2)',
          maxWidth: 520,
          mx: 2,
        }}
      >
        {/* Animated gear icon */}
        <Box
          sx={{
            width: 120, height: 120, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 193, 7, 0.1) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid rgba(255, 152, 0, 0.3)',
            animation: 'spin 8s linear infinite',
            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
          }}
        >
          <Build sx={{ fontSize: 56, color: '#ff9800' }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            background: 'linear-gradient(135deg, #ff9800, #ffc107)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {isArabic ? 'تحت الصيانة' : 'Under Maintenance'}
        </Typography>

        {/* Message */}
        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.8 }}>
          {isArabic
            ? 'نعمل على تحسين النظام. سنعود قريباً إن شاء الله.'
            : 'We are improving the system. We will be back soon.'}
        </Typography>

        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          {isArabic
            ? 'إذا كنت مدير النظام، سجل دخولك للوصول الكامل.'
            : 'If you are the system admin, log in for full access.'}
        </Typography>

        {/* Refresh Button */}
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          sx={{
            color: '#ff9800',
            borderColor: 'rgba(255, 152, 0, 0.5)',
            borderRadius: 3,
            px: 4, py: 1.2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            '&:hover': {
              borderColor: '#ff9800',
              bgcolor: 'rgba(255, 152, 0, 0.08)',
            }
          }}
        >
          {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
        </Button>

        {/* Countora branding */}
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.2)', mt: 2 }}>
          Countora ERP System
        </Typography>
      </Stack>
    </Box>
  );
};

export default MaintenancePage;